import Styles from './Sections.module.scss'
import { SectionProps } from './Types'
import { POPULAR_CARD_OVERRIDES } from '../constants'
import { NewDesignButtonV2, NewDocumentButtonV2, WizardButton, WizardCard } from '../WizardButton/WizardButton'
import { WizardCategoryCardGrid, WizardPopularBanner } from '../WizardCategoryCard/WizardCategoryCard'
import { Button } from 'blaze-ui/Button'
import { ArrowRight } from 'blaze-ui/icons'
import { AllSubCategories, CategoryTypes } from '../Types'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { ContentType } from 'components/ContentType/ContentType'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { PressProps } from '@react-aria/interactions'
import { TurnIntoContent } from './AdvancedTools'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'

const BlogPostButton = ({ onPress }: PressProps) => {
  return (
    <Button variant="secondary" onPress={onPress}>
      Generate Blog Post
    </Button>
  )
}

export const Popular = ({ recentWizards, ...props }: SectionProps) => {
  const isVirtualMarketer = useCurrentWorkspace()?.is_virtual_marketer
  const subCategory = props.subCategory
  const setSubCategory = props.setSubCategory
  const setSelectedCategory = props.setSelectedCategory
  const setPrevCategory = props.setPrevCategory

  const navigate = (category: CategoryTypes, subCategory?: AllSubCategories) => {
    if (subCategory) {
      setPrevCategory('Popular')
    }
    setSelectedCategory(category)
    setSubCategory(subCategory ?? undefined)
  }

  if (subCategory === 'turn_into') {
    return (
      <div className={Styles.commonSectionContainer}>
        <TurnIntoContent {...props} />
      </div>
    )
  }

  return (
    <div className={Styles.popularContainer}>
      <WizardCategoryCardGrid
        variant="popular"
        heading="Social Media"
        button={
          <Button variant="ghost" endIcon={ArrowRight} onPress={() => navigate('Social media')}>
            More Social Media Platforms
          </Button>
        }
      >
        <WizardCard
          size="pop-sm"
          title="Instagram"
          description="Stories, Posts, and Reels"
          image="https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/v2_card_images/popular/Social_ht1h23.png"
          onPress={() => navigate('Social media', 'instagram')}
        />
        <WizardCard
          size="pop-sm"
          title="Facebook"
          description="Stories, Posts, and Reels"
          image="https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/v2_card_images/popular/Social-1_jeolj3.png"
          onPress={() => navigate('Social media', 'facebook')}
        />
        <WizardCard
          size="pop-sm"
          title="LinkedIn"
          description="Text Posts, Visual Posts, Ads"
          image="https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/v2_card_images/popular/Social-2_u537jx.png"
          onPress={() => navigate('Social media', 'linkedIn')}
        />
        <WizardCard
          size="pop-sm"
          title="X/Twitter"
          description="Posts, Threads, Profile Pictures"
          image="https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/v2_card_images/popular/Social-3_gsmztu.png"
          onPress={() => navigate('Social media', 'twitter')}
        />
      </WizardCategoryCardGrid>

      <WizardCategoryCardGrid variant="popular" heading="Marketplace">
        <WizardCard
          size="pop-wide"
          title="Product Descriptions"
          description="Generate product descriptions for multiple marketplace websites at once."
          image="https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/v2_card_images/popular/Social-4_hq5d9e.png"
          onPress={() => navigate('Marketplaces', 'product_descriptions')}
        />
        <WizardCard
          size="pop-wide"
          title="Product Visuals"
          description="Get some help to make your product photos look like you spent thousands on a photographer."
          image="https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/v2_card_images/popular/Social-5_ujrbwi.png"
          onPress={() => navigate('Marketplaces', 'visual')}
        />
      </WizardCategoryCardGrid>

      <WizardCategoryCardGrid
        variant="popular"
        heading="Video"
        button={
          <Button variant="ghost" endIcon={ArrowRight} onPress={() => navigate('Video')}>
            See All Video Content Options
          </Button>
        }
      >
        <WizardCard
          size="pop-wide"
          title="YouTube"
          description="Create video posts, generate scripts for videos and use professional video templates."
          image="https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/v2_card_images/popular/Social-6_g3qipf.png"
          onPress={() => navigate('Video', 'youtube')}
        />
        <WizardCard
          size="pop-wide"
          title="TikTok"
          description="Create video posts, generate scripts for videos and use professional video templates."
          image="https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/v2_card_images/popular/Social-7_qki4cr.png"
          onPress={() => navigate('Video', 'tiktok')}
        />
      </WizardCategoryCardGrid>

      <WizardPopularBanner
        title="Blog Post & SEO Builder"
        description="Write SEO-optimized blog posts that will ensure you show up in Google search results."
        image="https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/v2_card_images/popular/Social-8_hsmhu2.png"
        variant="blog"
        actions={
          <>
            <ContentType {...props} type={ChannelType.BlogPost}>
              <BlogPostButton />
            </ContentType>
            <Button variant="editor" endIcon={ArrowRight} onPress={() => navigate('Blog/SEO')}>
              See All Content Types
            </Button>
          </>
        }
      />

      <WizardCategoryCardGrid
        variant="popular"
        heading="Brainstorm Ideas"
        button={
          <Button variant="ghost" endIcon={ArrowRight} onPress={() => navigate('Brainstorm')}>
            See All
          </Button>
        }
      >
        <WizardButton
          size="pop-wide"
          title="Brainstorm Content Ideas"
          description="Quickly generate ideas for social media, emails, blog posts and other platforms"
          image="https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/v2_card_images/popular/Social-9_j0mkx3.png"
          channelKey={GenerationType.Brainstorm}
          version={2}
        />
        <WizardButton
          size="pop-wide"
          title="Build a Content Plan"
          description="Analyze your business' website to get content ideas on how to market your products and services."
          image="https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/v2_card_images/popular/Social-10_qi2gfr.png"
          channelKey={GenerationType.ContentPlan}
          version={2}
        />
      </WizardCategoryCardGrid>

      <WizardCategoryCardGrid variant="popular" heading="Start with a clean slate">
        <NewDesignButtonV2
          size="pop-design"
          image="https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/v2_card_images/popular/Social-11_zlhrww.png"
        />
        <NewDocumentButtonV2
          size="pop-design"
          image="https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/v2_card_images/popular/Social-11_zlhrww.png"
        />
      </WizardCategoryCardGrid>

      <WizardCategoryCardGrid
        variant="popular"
        heading="Advanced Tools"
        button={
          <Button variant="ghost" endIcon={ArrowRight} onPress={() => navigate('Tools')}>
            See All
          </Button>
        }
      >
        <WizardButton
          size="pop-wide"
          title="Multi-Channel Campaign"
          description="Quickly generate ideas for social media, emails, blog posts and other materials"
          image="https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/v2_card_images/popular/Social-13_cooewg.png"
          channelKey={GenerationType.Campaign}
          version={2}
        />
        {!isVirtualMarketer && (
          <WizardButton
            size="pop-wide"
            title="Multiple Instagram Posts"
            description="Generate, schedule, and publish Instagram posts for a whole month."
            image="https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/v2_card_images/popular/Social-14_dywlew.png"
            channelKey={GenerationType.InstagramPlanning}
            version={2}
            cardInfoOverride={POPULAR_CARD_OVERRIDES}
          />
        )}
        {!isVirtualMarketer && (
          <WizardButton
            size="pop-wide"
            title="Multiple Blog Posts"
            description="Generate, schedule, and publish multiple blog posts for a whole month."
            image="https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/v2_card_images/popular/Social-15_ny8bce.png"
            channelKey={GenerationType.SEOPlanning}
            version={2}
          />
        )}
        <WizardCard
          size="pop-wide"
          title="Turn Anything into Content"
          description="Blaze will analyze your business website and give you ideas on how to market your products & services"
          image="https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/v2_card_images/popular/Social-16_vjuyjy.png"
          onPress={() => navigate('Popular', 'turn_into')}
        />
      </WizardCategoryCardGrid>

      <WizardPopularBanner
        title="Generate Copy for Ads"
        description="Blaze supports the most popular social media and search platforms."
        image="https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/v2_card_images/popular/Social-17_mw8phc.png"
        variant="ads"
        actions={
          <Button variant="secondary" onPress={() => navigate('Ads')}>
            Generate Copy
          </Button>
        }
      />

      <WizardCategoryCardGrid variant="popular" heading="Emails">
        <WizardCard
          size="pop-wide"
          title="Newsletters"
          description="Keep your audience hooked with updates and stories they'll love."
          image="https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/v2_card_images/popular/Social-18_xhlym8.png"
          onPress={() => navigate('Emails', 'newsletters')}
        />
        <WizardCard
          size="pop-wide"
          title="Lifecycle"
          description="Guide new visitors into happy customers with perfectly timed emails."
          image="https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/v2_card_images/popular/Social-19_kqvcex.png"
          onPress={() => navigate('Emails', 'lifecycle')}
        />
        <WizardCard
          size="pop-wide"
          title="Ecommerce"
          description="Turn shoppers into buyers with smart, automated messages."
          image="https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/v2_card_images/popular/Social-20_ypwyr5.png"
          onPress={() => navigate('Emails', 'ecommerce')}
        />
        <WizardCard
          size="pop-wide"
          title="Sales"
          description="Close more deals with persuasive emails that drive action."
          image="https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/v2_card_images/popular/Social-21_rgpyjc.png"
          onPress={() => navigate('Emails', 'sales')}
        />
      </WizardCategoryCardGrid>

      <WizardCategoryCardGrid variant="popular" heading="Other">
        <WizardCard
          size="pop-wide"
          title="Websites"
          description="Generate copy for the most common website pages."
          image="https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/v2_card_images/popular/Social-22_warzcm.png"
          onPress={() => navigate('Other', 'website')}
        />
        <WizardCard
          size="pop-wide"
          title="Sales"
          description="Create sales documents to help you sell your product or business."
          image="https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/v2_card_images/popular/Social-23_gtf7ux.png"
          onPress={() => navigate('Other', 'sales')}
        />
        <WizardCard
          size="pop-wide"
          title="Customer Support"
          description="Everything you need to support your customers (reply templates, FAQs, etc.)"
          image="https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/v2_card_images/popular/Social-24_ah5yyd.png"
          onPress={() => navigate('Other', 'customer_support')}
        />
        <WizardCard
          size="pop-wide"
          title="Recruiting"
          description="Close more candidates with persuasive emails that drive action."
          image="https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/v2_card_images/popular/Social-25_ulxm91.png"
          onPress={() => navigate('Other', 'recruiting')}
        />
      </WizardCategoryCardGrid>
    </div>
  )
}
